import { lazy, Suspense, useEffect, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { trackPageview } from './lib/trackEvent';
import './styles/global.css';
import Header from './components/Header';
import NewsTicker from './components/NewsTicker';
import Hero from './components/Hero';
import SurveyTracker from './components/SurveyTracker';
import QuizModule from './components/Quiz/QuizModule';
import MapModule from './components/DistrictMap/MapModule';
import CandidatesModule from './components/Candidates/CandidatesModule';
import ProposalsModule from './components/Proposals/ProposalsModule';
import Sidebar from './components/Sidebar/Sidebar';
import TurnoutCubeSection from './components/Turnout/TurnoutCubeSection';
import LiveFeed from './components/ElectionDay/LiveFeed';
import LatestNewsTiles from './components/Dashboard/LatestNews/LatestNewsTiles';
import SectionTiles from './components/SectionTiles/SectionTiles';
import SpuntStripBanner from './components/SpuntBanner/SpuntStripBanner';
import VotmtBanner from './components/VotmtBanner/VotmtBanner';
import LiveDebatePlayer from './components/LiveDebate/LiveDebatePlayer';
import VideosStrip from './components/VideosStrip/VideosStrip';
import Footer from './components/Footer';
import SurveysPage from './components/Surveys/SurveysPage';
// Admin tree is lazy-loaded — every admin component (count-sheet,
// photo-extract, election-phase, all forms) hides behind this single
// boundary. Anonymous readers (the vast majority of traffic) skip the
// entire admin bundle; admins get a brief Suspense fallback when they
// navigate to /admin/*. Same pattern as AnalysisPage + District
// AnalysisPage below. Verified safe: grep confirms App.tsx is the
// ONLY import site for src/components/Admin/AdminPage in src/.
const AdminPage = lazy(() => import('./components/Admin/AdminPage'));
import FeatureSection from './components/FeatureSection';
import KnowBeforeYouVotePage from './components/KnowBeforeYouVote/KnowBeforeYouVotePage';
import CandidateProfilePage from './components/KnowBeforeYouVote/CandidateProfilePage';
import QuizResultPage from './components/Quiz/QuizResultPage';
import ElectionDayPage from './components/ElectionDay/ElectionDayPage';
import TurnoutPage from './components/Turnout/TurnoutPage';
import ExplorePage from './components/Explore/ExplorePage';
import ManifestoExplorer from './components/Manifesto/ManifestoExplorer';
import ManifestoPromptStrip from './components/Manifesto/ManifestoPromptStrip';
import ResultsMap from './components/Results/ResultsMap';
import ResultsPage from './components/Results/ResultsPage';
import OfficialResultsPage, { OfficialResultsBody } from './components/Results/OfficialResultsPage';
import WhosElectedBar from './components/Results/WhosElectedBar';
import RogueSamplingSummary from './components/Results/RogueSamplingSummary';
import CountingStream from './components/ElectionDay/CountingStream';
import AndreaTestPage from './components/AndreaTestPage';
import AndreaMapPage from './components/AndreaMapPage';
import HunchMainStrip from './components/HunchEmbed/HunchMainStrip';
import LiveCore from './components/LiveCore';
import './components/LiveBand.css';
import { useEffectivePhase } from './lib/useEffectivePhase';
import { useElectionDayTurnout } from './lib/useElectionDayTurnout';
import { hasTurnoutData } from './data/electionday-turnout-types';

// Analysis workbench: lazy-loaded so Recharts + Papa Parse + the workbench
// itself don't ship in the public dashboard bundle. Route 404s in prod
// anyway; lazy-loading keeps the dev-only chunk separate.
const AnalysisPage = lazy(() => import('./components/Analysis/AnalysisPage'));

// District-by-district long-read: lazy-loaded so the inline SVG charts,
// the 14 KB district JSON, and the editorial CSS don't bloat the
// main bundle. Unlisted — reachable only by direct URL for now.
// TODO: add a header/nav link to /district_analysis once the editor
// signs off on the long-read content.
const DistrictAnalysisPage = lazy(
  () => import('./components/DistrictAnalysis/DistrictAnalysisPage'),
);

// Tiny in-app router. The dashboard is a Vite SPA served behind a
// catch-all rewrite (vercel.json maps every path to /index.html), so we
// just read window.location.pathname and re-read it on popstate so the
// browser's back/forward buttons keep working.
function usePathname(): string {
  const [path, setPath] = useState(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return path;
}

// Self-hosted pageview tracker — fires on initial mount + every route
// change. Skips /admin/* and /analysis/* so editor traffic never lands
// in the public-side metrics. The parallel @vercel/analytics integration
// is unaffected — both track in parallel.
function usePageviewTracker(path: string): void {
  useEffect(() => {
    if (path.startsWith('/admin') || path.startsWith('/analysis')) return;
    trackPageview(path);
  }, [path]);
}

// Watches the same breakpoint at which page-grid collapses to a single
// column (≤1299 px), so the Sidebar can be relocated to the last
// position on mobile while staying in the right rail on desktop.
const MOBILE_QUERY = '(max-width: 1299px)';
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function App() {
  const path = usePathname();
  usePageviewTracker(path);
  // useEffectivePhase wraps useElectionPhase with a client-side URL
  // override (`?phase=results_pending_rogue` sticky in localStorage)
  // so the admin team can preview a phase composition without
  // flipping the production DB phase. Override never crosses the
  // browser boundary — see useEffectivePhase.ts for the contract.
  const { phase } = useEffectivePhase();

  if (path === '/surveys' || path === '/surveys/') {
    return <SurveysPage />;
  }

  if (path === '/quiz/result' || path === '/quiz/result/') {
    return <QuizResultPage />;
  }

  if (path === '/electionday' || path === '/electionday/') {
    return <ElectionDayPage />;
  }

  if (path === '/turnout' || path === '/turnout/') {
    return <TurnoutPage />;
  }

  if (path === '/explore' || path === '/explore/') {
    return <ExplorePage />;
  }

  if (path === '/results' || path === '/results/') {
    return <ResultsPage />;
  }

  if (path === '/official-results' || path === '/official-results/') {
    return <OfficialResultsPage />;
  }

  if (path === '/andrea_test' || path === '/andrea_test/') {
    return <AndreaTestPage />;
  }

  if (path === '/andrea_map' || path === '/andrea_map/') {
    return <AndreaMapPage />;
  }

  // Unlisted tool — reachable only by direct URL. No FeatureSection
  // gate here on purpose: the homepage `manifesto-strip` flag is the
  // real reader-facing on/off switch, and the server-side
  // `traffic_kill_switch` in manifesto_settings is a harder kill if we
  // ever need to disable the API in a hurry. Wrapping the route in
  // FeatureSection caused a flash-then-disappear when the wrapper's
  // preview-bypass localStorage didn't survive the strip → explorer
  // navigation in some browsers.
  if (path === '/ai_manifesto' || path === '/ai_manifesto/') {
    return <ManifestoExplorer />;
  }

  if (path === '/know-before-you-vote' || path === '/know-before-you-vote/') {
    return <KnowBeforeYouVotePage />;
  }

  if (path.startsWith('/know-before-you-vote/')) {
    const slug = path.replace('/know-before-you-vote/', '').replace(/\/$/, '');
    return <CandidateProfilePage slug={slug} />;
  }

  if (path === '/admin' || path.startsWith('/admin/')) {
    return (
      <Suspense fallback={<div style={{ padding: 40 }}>Loading admin…</div>}>
        <AdminPage path={path} />
      </Suspense>
    );
  }

  if (path === '/analysis' || path === '/analysis/') {
    return (
      <Suspense fallback={<div style={{ padding: 40 }}>Loading workbench…</div>}>
        <AnalysisPage />
      </Suspense>
    );
  }

  if (path === '/district_analysis' || path === '/district_analysis/') {
    return (
      <Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <DistrictAnalysisPage />
      </Suspense>
    );
  }

  // Homepage fall-through. Stage 1 wired polls_open; Stage 3A added
  // results_pending + counting; Stage A (rogue) adds the sampling-
  // driven results_pending_rogue variant.
  switch (phase) {
    case 'polls_open':
      return <HomepagePollsOpen />;
    case 'results_pending':
      return <HomepageResultsPending />;
    case 'results_pending_rogue':
      return <HomepageResultsPendingRogue />;
    case 'counting':
      return <HomepageCounting />;
    case 'counting_over':
      return <HomepageCountingOver />;
    case 'official_results':
      return <HomepageOfficialResults />;
    case 'post_election':
      return <HomepagePostElection />;
    case 'normal':
    default:
      return <HomepageNormal />;
  }
}

// ─── Post-10pm compositions (Stage 3B — live-led layout) ──────────
//
// Both results_pending and counting strip the side rails and give the
// space to a "live core" — the counting-hall stream embed and the
// LiveFeed blog, side-by-side on desktop (stream ~60%, blog ~40%),
// stacked on mobile (stream top, blog below). The CountingStream
// component self-hides when no URL is configured, so the slot is
// invisible until the editor pastes one.
//
// Below the live core: the latest developments —
//   • results_pending → ResultsMap (empty/awaiting) under the stream;
//                       HomepageTurnoutSection further down.
//   • counting        → ResultsMap under the stream + WhosElectedBar
//                       (consolidated national rollup + per-district
//                       elected cards in ONE wide section).
//
// Secondary sections stay collapsed in SectionTiles; ad strips stay
// at the bottom. Hero precedence (winner > turnout > default) is
// unchanged — handled inside Hero via the phase prop.
//
// normal/polls_open compositions are NOT touched here.

function HomepageResultsPending() {
  return (
    <>
      <div className="site-top">
        <Header />
        <FeatureSection sectionKey="news_ticker" label="News ticker">
          <NewsTicker />
        </FeatureSection>
      </div>
      <FeatureSection sectionKey="hero" label="Hero">
        <Hero phase="results_pending" />
      </FeatureSection>

      {/* ── Live core: counting-hall stream + blog, side-by-side
          on desktop. Below the stream we mount the results map
          early — pre-count, it shows the empty district shapes so
          readers see the structure that's about to fill in once
          counting starts. Same slot the counting phase uses, just
          with no data flowing into it yet. ── */}
      <section className="live-band live-band--core">
        <LiveCore
          stream={<CountingStream />}
          belowStream={<ResultsMap title="Results map · awaiting count" />}
          feed={<LiveFeed />}
        />
      </section>

      {/* Partner debate player — separate from the live-core; its
          own internal mini-player kicks in when scrolled past. */}
      <section className="live-band">
        <LiveDebatePlayer />
      </section>

      {/* Turnout — full-width band so the cube grid gets the same
          envelope as the news carousel rather than being squeezed by
          .page-main's 900px middle column. */}
      <section className="live-band">
        <HomepageTurnoutSection />
      </section>

      <FeatureSection sectionKey="videos_strip" label="Videos">
        <VideosStrip />
      </FeatureSection>

      <div className="page-grid">
        <main className="page-main page-main--full">
          <SectionTiles />
        </main>
      </div>

      {/* ── Partner / ad strips at the BOTTOM (per spec) ── */}
      <div className="page-grid">
        <main className="page-main page-main--full">
          <FeatureSection sectionKey="hunch_strip" label="Spunt fantasy football strip">
            <SpuntStripBanner />
          </FeatureSection>
          <FeatureSection sectionKey="votmt_banner" label="vot.mt banner">
            <VotmtBanner />
          </FeatureSection>
        </main>
      </div>

      <Footer />
      <Analytics />
    </>
  );
}

// ── Results-pending Rogue ─────────────────────────────────────────
//
// Stage A variant of results_pending. Same bands top-to-bottom,
// but the LiveCore feed slot is REPLACED by the RogueSamplingSummary
// panel (Lovin Malta's live sampling certainty readout). The
// LiveFeed (newsroom blog) drops to its own .live-band below the
// live-core so editorial coverage isn't lost — it just moves.
//
// Honesty: this composition is a DISPLAY layer over the existing
// sampling pipeline. The hero winner announcement is unchanged —
// it stays the existing editorial nationalCall (winner > turnout >
// default precedence). Even at the panel's PROJECTED state, the
// homepage hero shows whatever nationalCall has, which only
// changes when a human flips it via /admin/announce. The rogue
// panel reads sampling state; it never writes nationalCall. The
// statistical reading is informational; the call is editorial.

function HomepageResultsPendingRogue() {
  return (
    <>
      <div className="site-top">
        <Header />
        <FeatureSection sectionKey="news_ticker" label="News ticker">
          <NewsTicker />
        </FeatureSection>
      </div>
      <FeatureSection sectionKey="hero" label="Hero">
        <Hero phase="results_pending" />
      </FeatureSection>

      {/* ── Live core — stream stays, feed slot becomes the sampling
          summary panel. ── */}
      <section className="live-band live-band--core">
        <LiveCore
          stream={<CountingStream />}
          feed={<RogueSamplingSummary />}
        />
      </section>

      {/* ── LiveFeed — moved down from the LiveCore feed slot into
          its own band so editorial coverage stays prominent below
          the sampling panel. ── */}
      <section className="live-band">
        <LiveFeed />
      </section>

      {/* Partner debate player — same band as in results_pending. */}
      <section className="live-band">
        <LiveDebatePlayer />
      </section>

      {/* Turnout — unchanged from results_pending. */}
      <section className="live-band">
        <HomepageTurnoutSection />
      </section>

      <FeatureSection sectionKey="videos_strip" label="Videos">
        <VideosStrip />
      </FeatureSection>

      <div className="page-grid">
        <main className="page-main page-main--full">
          <SectionTiles />
        </main>
      </div>

      {/* ── Partner / ad strips at the BOTTOM (per spec) ── */}
      <div className="page-grid">
        <main className="page-main page-main--full">
          <FeatureSection sectionKey="hunch_strip" label="Spunt fantasy football strip">
            <SpuntStripBanner />
          </FeatureSection>
          <FeatureSection sectionKey="votmt_banner" label="vot.mt banner">
            <VotmtBanner />
          </FeatureSection>
        </main>
      </div>

      <Footer />
      <Analytics />
    </>
  );
}

// Stage 1 — counting sibling phases. counting / counting_over /
// official_results all render the SAME composition; Hero copy varies
// (handled inside Hero.tsx) and `official_results` adds a prominent
// "Official results →" link band right under the hero. No three
// near-duplicate compositions.
type CountingPhase = 'counting' | 'counting_over' | 'official_results';

function HomepageCounting() {
  return <HomepageCountingBase phase="counting" />;
}
function HomepageCountingOver() {
  return <HomepageCountingBase phase="counting_over" />;
}
function HomepageOfficialResults() {
  return <HomepageCountingBase phase="official_results" />;
}

function HomepageCountingBase({ phase }: { phase: CountingPhase }) {
  const isOfficial = phase === 'official_results';
  return (
    <>
      <div className="site-top">
        <Header />
        <FeatureSection sectionKey="news_ticker" label="News ticker">
          <NewsTicker />
        </FeatureSection>
      </div>
      <FeatureSection sectionKey="hero" label="Hero">
        <Hero phase={phase} />
      </FeatureSection>

      {/* Official results CTA — only on the `official_results`
          phase. A prominent band linking to the new /official-
          results page. Sits directly under the hero so it's the
          first thing readers see after the headline. */}
      {isOfficial && (
        <section className="live-band">
          <a
            href="/official-results"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 18,
              padding: '18px 24px',
              background: 'linear-gradient(135deg, #003da5, #0058d6)',
              color: '#fff',
              borderRadius: 10,
              textDecoration: 'none',
              fontFamily: "var(--font-display, 'Poppins', sans-serif)",
              boxShadow: '0 10px 28px rgba(0,61,165,0.22)',
            }}
          >
            <span>
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  opacity: 0.78,
                }}
              >
                Electoral Commission
              </span>
              <span
                style={{
                  display: 'block',
                  marginTop: 4,
                  fontSize: 22,
                  fontWeight: 900,
                  letterSpacing: -0.01,
                }}
              >
                Read the official results →
              </span>
            </span>
            <span
              aria-hidden="true"
              style={{
                fontSize: 32,
                fontWeight: 900,
                opacity: 0.8,
              }}
            >
              →
            </span>
          </a>
        </section>
      )}

      {/* ── Live core: counting-hall stream + live blog, with the
          results map directly under the stream at the same width
          (LiveCore.belowStream). The feed scrolls past the map on
          the right. LiveDebatePlayer is rendered as its own band
          below — keeps the live-core's pinned-mini behaviour clean
          (its banner would otherwise overlap the stream when both
          go mini-corner). ── */}
      <section className="live-band live-band--core">
        <LiveCore
          stream={<CountingStream />}
          belowStream={<ResultsMap title="Live results map" />}
          feed={<LiveFeed />}
        />
      </section>

      {/* Who's-elected bar — runs the full width of the live-band
          above it (map + feed columns). District selector +
          carousel of most-recently-elected candidates. */}
      <section className="live-band">
        <WhosElectedBar />
      </section>

      {/* Partner debate player — separate band, own mini-player. */}
      <section className="live-band">
        <LiveDebatePlayer />
      </section>

      <FeatureSection sectionKey="videos_strip" label="Videos">
        <VideosStrip />
      </FeatureSection>

      {/* SectionTiles now also surfaces Turnout / Spunt / vot.mt as
          condensed tiles — the standalone full-width sections that
          used to host them on this phase are gone (they competed for
          attention with the live core). */}
      <div className="page-grid">
        <main className="page-main page-main--full">
          <SectionTiles />
        </main>
      </div>

      <Footer />
      <Analytics />
    </>
  );
}

// ─── Post-election archive composition ───────────────────────────
//
// Election is over. The homepage becomes a static record:
//   Header + News ticker + Hero (post_election framing)
//   + the same map / summary / vote-flow body that powers
//   /official-results, mounted inline (showIntro=false — the Hero
//   already carries the framing copy)
//   + Footer.
//
// NO LiveCore, LiveFeed, LiveDebatePlayer, CountingStream,
// SectionTiles, SpuntStripBanner, VotmtBanner — the live-blog era
// is done. The same FeatureSection gates that protect
// /official-results apply here, so editorial can still hide any of
// the three blocks from /admin/flags if they need to.

function HomepagePostElection() {
  return (
    <>
      <div className="site-top">
        <Header />
      </div>
      <FeatureSection sectionKey="hero" label="Hero">
        <Hero phase="post_election" />
      </FeatureSection>
      <OfficialResultsBody showIntro={false} />
      <Footer />
      <Analytics />
    </>
  );
}

function HomepageNormal() {
  const isMobile = useIsMobile();
  return (
    <>
      <div className="site-top">
        <Header />
        <FeatureSection sectionKey="news_ticker" label="News ticker">
          <NewsTicker />
        </FeatureSection>
      </div>
      <FeatureSection sectionKey="hero" label="Hero">
        <Hero />
      </FeatureSection>
      <div className="page-grid">
        <FeatureSection sectionKey="latest_news" label="Latest news">
          <LatestNewsTiles />
        </FeatureSection>
        <main className="page-main">
          {/* Homepage funnel for /ai_manifesto — reader types a question
              and is taken to the explorer with the query pre-filled and
              auto-submitted. Gated by the `manifesto-strip` flag so it
              can be hidden without code churn. To take the underlying
              tool down: manifesto-explorer flag (page-level kill) or
              traffic_kill_switch (server-level kill) in /admin/manifestos. */}
          <FeatureSection sectionKey="manifesto-strip" label="Manifesto prompt strip">
            <ManifestoPromptStrip />
          </FeatureSection>
          {/* Live-debate player — top of the main column. Renders nothing
              unless an editor has saved a Facebook video URL at
              /admin/live-debate. Self-contained: no FeatureSection
              wrapper, the empty-URL case hides itself. */}
          <LiveDebatePlayer />
          <FeatureSection sectionKey="survey_tracker" label="Survey tracker">
            <SurveyTracker />
          </FeatureSection>
          {/* Strip slot — Spunt × Lovin Malta Fantasy Election
              League promo banner. The flag KEY stays `hunch_strip`
              (renaming it would need a DB migration); it still works
              as the on/off toggle for this slot. The hero hunch poll
              embed is untouched (Hero.tsx, hero_hunch). */}
          <FeatureSection sectionKey="hunch_strip" label="Spunt fantasy football strip">
            <SpuntStripBanner />
          </FeatureSection>
          <FeatureSection sectionKey="quiz" label="Quiz">
            <QuizModule />
          </FeatureSection>
          {/* vot.mt partner promo — image link-out to vot.mt/elections. */}
          <FeatureSection sectionKey="votmt_banner" label="vot.mt banner">
            <VotmtBanner />
          </FeatureSection>
          {/* Map moved up — readers see their district / past results
              before drilling into proposals + candidates. */}
          <FeatureSection sectionKey="district_map" label="District map">
            <MapModule />
          </FeatureSection>
          <FeatureSection sectionKey="proposals" label="Proposals">
            <ProposalsModule />
          </FeatureSection>
        </main>
        {/* Sidebar (Instagram reels) sits in the right rail on
            desktop and moves to the very bottom on mobile (after
            videos + candidates) — see useIsMobile branch below. */}
        {!isMobile && (
          <FeatureSection sectionKey="sidebar" label="Sidebar">
            <Sidebar />
          </FeatureSection>
        )}
      </div>

      {/* Videos strip — full-bleed dark band that breaks the page
          between the rail-flanked main column and the wide candidates
          section below. */}
      <FeatureSection sectionKey="videos_strip" label="Videos">
        <VideosStrip />
      </FeatureSection>

      <div className="page-grid">
        <main className="page-main page-main--full">
          <FeatureSection sectionKey="candidates" label="Candidates">
            <CandidatesModule />
          </FeatureSection>
        </main>
      </div>
      {isMobile && (
        <FeatureSection sectionKey="sidebar" label="Sidebar">
          <Sidebar />
        </FeatureSection>
      )}
      <Footer />
      {/* Vercel Analytics — automatic page-view tracking. Only fires
          in production via window.location, no impact on dev. */}
      <Analytics />
    </>
  );
}

// ─── Polls-open composition ───────────────────────────────────────
//
// Same overall page-grid as HomepageNormal (left rail / main / right
// rail, then full-bleed videos + candidates). Phase-specific shifts:
//   • Hero renders its polls_open variant (LIVE indicator + close
//     countdown).
//   • Live focus at top of main column: Instagram trail as a tile
//     grid (was the right-rail reels stack on Normal), followed by
//     the live-debate player.
//   • Context modules (survey, spunt, quiz, vot.mt, map, proposals)
//     move below the live block but keep their relative order and
//     their individual FeatureSection flag gates.
//   • Right rail trims to ManifestoPromptStrip — editors keep a
//     lean rail and FeaturedArticles is omitted (still flagged for
//     Normal). Mobile order matches Normal's convention: live first,
//     rail content last.
//
// Every existing FeatureSection wrapper is preserved on its module
// so /admin/flags toggles still work in this composition. The
// Instagram tile grid carries the existing `sidebar` flag — same
// editor-side on/off toggle that controlled the right-rail reels.
function HomepagePollsOpen() {
  return (
    <>
      <div className="site-top">
        <Header />
        <FeatureSection sectionKey="news_ticker" label="News ticker">
          <NewsTicker />
        </FeatureSection>
      </div>
      <FeatureSection sectionKey="hero" label="Hero">
        <Hero phase="polls_open" />
      </FeatureSection>

      {/* ── Below-hero LIVE BAND (polls_open only) ────────────────
          Full-width 2-column band. Left column stacks two rows —
          LatestNewsTiles in horizontal row layout (3 across on
          desktop), then InstagramReels in grid layout (3 across,
          same manually-pinned IDs from site_embeds, NOT an auto-
          feed). Right column hosts the HunchMainEmbed widget that
          used to live in the hero; column sized 3/10 of the band
          so the widget visually balances the two rows beside it.
          On mobile (≤999 px) the band stacks: news → insta →
          hunch.

          FeatureSection flag wrappers preserved on the moved
          elements:
            · `latest_news` — LatestNewsTiles (was left rail)
            · `sidebar`     — InstagramReels (was main column)
            · `hero_hunch`  — HunchMainEmbed (was hero, third col) */}
      {/* ── News carousel — single row of 4 cards, scroll horizontal
          for the 5th+. Full-band width. ── */}
      <section className="live-band" aria-label="Latest news">
        <FeatureSection sectionKey="latest_news" label="Latest news">
          <LatestNewsTiles layout="carousel" showHeader={false} />
        </FeatureSection>
      </section>

      <div className="page-grid">
        <main className="page-main page-main--full">
          {/* Live-debate player — self-hides when no URL is set. */}
          <LiveDebatePlayer />
        </main>
      </div>

      {/* ── Turnout ── highest-priority data section after news.
          Full-width band like the news carousel so the cube grid
          gets the wider envelope. Self-hides when no editor figures
          are entered. */}
      <section className="live-band">
        <HomepageTurnoutSection />
      </section>

      <div className="page-grid">
        <main className="page-main page-main--full">
          {/* ── Hunch strip ── sits immediately after turnout. */}
          <FeatureSection sectionKey="hero_hunch" label="Hunch.mt live poll">
            <HunchMainStrip />
          </FeatureSection>

          {/* (Instagram carousel removed from polls_open per the
              latest spec — the `sidebar` flag still controls the
              normal-phase right-rail reels block; it just no
              longer mounts here.) */}

          {/* ── Manifesto prompt strip ── above survey tracker. */}
          <FeatureSection sectionKey="manifesto-strip" label="Manifesto prompt strip">
            <ManifestoPromptStrip />
          </FeatureSection>
          <FeatureSection sectionKey="survey_tracker" label="Survey tracker">
            <SurveyTracker />
          </FeatureSection>
          <FeatureSection sectionKey="hunch_strip" label="Spunt fantasy football strip">
            <SpuntStripBanner />
          </FeatureSection>
          <FeatureSection sectionKey="quiz" label="Quiz">
            <QuizModule />
          </FeatureSection>
          <FeatureSection sectionKey="votmt_banner" label="vot.mt banner">
            <VotmtBanner />
          </FeatureSection>
          <FeatureSection sectionKey="district_map" label="District map">
            <MapModule />
          </FeatureSection>
          <FeatureSection sectionKey="proposals" label="Proposals">
            <ProposalsModule />
          </FeatureSection>
        </main>
      </div>

      <FeatureSection sectionKey="videos_strip" label="Videos">
        <VideosStrip />
      </FeatureSection>

      <div className="page-grid">
        <main className="page-main page-main--full">
          <FeatureSection sectionKey="candidates" label="Candidates">
            <CandidatesModule />
          </FeatureSection>
        </main>
      </div>
      <Footer />
      <Analytics />
    </>
  );
}

// ─── Turnout section ──────────────────────────────────────────────
//
// Data-driven visibility: appears as soon as the editor saves the
// first national pct at /admin/turnout; before that, returns null
// (the homepage layout flows naturally without it). The editor's
// label ("As of 14:00") and note (if any) are surfaced verbatim.
//
// The TurnoutTileGrid component owns its own per-district rendering
// + click-to-open info panel — we just wrap it in a section header
// and tail link here.
function HomepageTurnoutSection() {
  const { data: turnout } = useElectionDayTurnout();
  if (!hasTurnoutData(turnout)) return null;

  const headline = pickHeadlineLabel(turnout.national.pct_2pm, turnout.national.pct_10pm);

  return (
    <section
      id="turnout-section"
      aria-label="Turnout"
      style={{
        marginTop: 'var(--space-7, 3rem)',
        marginBottom: 'var(--space-6, 2rem)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-5, 1.5rem)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <p
            style={{
              fontFamily: "var(--font-display, 'Poppins', sans-serif)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: 'var(--red, #ff1349)',
              margin: 0,
            }}
          >
            Turnout · {turnout.label ?? 'Live'}
          </p>
          <h2
            style={{
              fontFamily: "var(--font-display, 'Poppins', sans-serif)",
              fontWeight: 900,
              fontSize: 'clamp(28px, 4vw, 40px)',
              letterSpacing: '-0.01em',
              textTransform: 'uppercase',
              color: 'var(--ink, #222)',
              margin: '6px 0 0',
              lineHeight: 1,
            }}
          >
            {headline}
          </h2>
        </div>
      </header>

      <TurnoutCubeSection />

      {turnout.note && (
        <p
          style={{
            fontFamily: 'var(--font-body, serif)',
            fontStyle: 'italic',
            fontSize: 14,
            color: 'var(--muted, #666)',
            margin: 'var(--space-5, 1.5rem) 0 0',
          }}
        >
          {turnout.note}
        </p>
      )}

      {/* Pill-button CTA — right-aligned to mirror the news
          carousel's "More on Lovin Malta →" placement above the
          turnout section. Inline styles to keep
          HomepageTurnoutSection self-contained (no extra CSS file
          for one button). */}
      <p style={{ margin: 'var(--space-5, 1.5rem) 0 0', textAlign: 'right' }}>
        <a
          href="/turnout"
          style={{
            display: 'inline-block',
            background: 'var(--red, #ff1349)',
            color: '#fff',
            fontFamily: "var(--font-display, 'Poppins', sans-serif)",
            fontWeight: 800,
            fontSize: 11,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            padding: '10px 18px',
            borderRadius: 999,
            textDecoration: 'none',
            boxShadow: '0 4px 14px rgba(255, 19, 73, 0.20)',
          }}
        >
          Full Breakdown →
        </a>
      </p>
    </section>
  );
}

function pickHeadlineLabel(pct_2pm: number | null, pct_10pm: number | null): string {
  // Prefer the most recent reading the editor has entered.
  if (pct_10pm !== null) return `${pct_10pm.toFixed(1)}% turned out`;
  if (pct_2pm !== null) return `${pct_2pm.toFixed(1)}% turnout at 2pm`;
  return 'Turnout';
}

